import Model from '@ember-data/model';
import attr from 'ember-data/attr';
import { computed } from '@ember/object';
import { equal } from '@ember/object/computed';
import { formatNumber } from 'cio/helpers/format-number';
import moment from 'moment-timezone';

export type ReceiptType = 'plan' | 'overages' | 'plan_and_overages';

export type ReceiptStatus =
  | 'unpaid'
  | 'paid'
  | 'draft' // Invoices that aren't finalised. These should not show in the UI.
  | 'overdue' // When a receipt is unpaid and its due date is in the past
  | 'uncollectible' // When a receipt is unpaid, we don't expect payment, but want to keep the payment option open
  | 'void'; // When a receipt has been marked void;

export default class ReceiptModel extends Model {
  @attr('string') title!: string;
  @attr('string') status!: ReceiptStatus;
  @attr('string') type!: ReceiptType;
  @attr('string') invoiceId!: string;
  @attr('number') invoicedAt!: number;
  @attr('number') total!: number;
  @attr('number') planPrice!: number;
  @attr('string') planInterval!: 'year' | 'month';
  @attr('string') planIntervalName!: 'year' | 'month' | 'quarter' | 'biannual';
  @attr('number') meteredEmail!: number;
  @attr('number') meteredProfiles!: number;
  @attr('number') meteredObjectProfiles!: number;
  @attr('number') overageMessageCount!: number;
  @attr('number') overageProfileCount!: number;
  @attr('number') overageObjectProfileCount!: number;
  @attr('string') displayName!: string;

  @equal('planInterval', 'year') isAnnual!: boolean;
  @equal('planInterval', 'month') isMonthly!: boolean;

  @equal('type', 'plan') isPlan!: boolean;
  @equal('type', 'overages') isOverages!: boolean;
  @equal('type', 'plan_and_overages') isPlanAndOverages!: boolean;

  @equal('status', 'paid') isPaid!: boolean;
  @equal('status', 'overdue') isOverdue!: boolean;

  // Need to check if the plan is CDP Starter so that we still show Monthly Subscription
  // on the receipts index without impacting other potential $0 receipts
  get isCdpStarter() {
    return this.title === 'CDP Starter';
  }

  @computed(
    'total',
    'planPrice',
    'planIntervalName',
    'overageProfileCount',
    'overageMessageCount',
    'overageObjectProfileCount',
    'isOnOrBeforeSwitchDate',
  )
  get description() {
    // Build a string like "Annual subscription + 100 additional profiles + 15,000 additional messages"
    let parts = [];

    if (this.planPrice > 0 || this.isCdpStarter) {
      let planIntervalDisplayNames = {
        month: 'Monthly Subscription',
        quarter: 'Quarterly Subscription',
        biannual: 'Biannual Subscription',
        year: 'Annual Subscription',
      };

      parts.push(planIntervalDisplayNames[this.planIntervalName]);
    }

    if (this.overageProfileCount > 0) {
      parts.push(`${formatNumber([this.overageProfileCount])} additional person profiles`);
    }

    if (this.overageMessageCount > 0) {
      // If the invoice date is on or before the switch date, we should use the word "messages" instead of "emails"
      parts.push(
        `${formatNumber([this.overageMessageCount])} additional ${this.isOnOrBeforeSwitchDate ? 'messages' : 'emails'}`,
      );
    }

    if (this.overageObjectProfileCount > 0) {
      parts.push(`${formatNumber([this.overageObjectProfileCount])} additional object profiles`);
    }

    return parts.join(' + ');
  }

  @computed('invoicedAt')
  get invoiceDate() {
    return moment.unix(this.invoicedAt).startOf('day');
  }

  @computed('invoiceDate')
  get isOnOrBeforeSwitchDate() {
    let comparisonDate = moment('2023-07-31').startOf('day').add(17, 'hours').add(30, 'minutes');
    return this.invoiceDate.isBefore(comparisonDate);
  }
}

// DO NOT DELETE: this is how TypeScript knows how to look up your models.
declare module 'ember-data/types/registries/model' {
  export default interface ModelRegistry {
    receipt: ReceiptModel;
  }
}
